/**
 * Read money amounts the way people actually type them: "about 400k",
 * "$1.2m", "40 thousand", "half a million", "250-300k", "(12,000)",
 * "we lost 20k", "none". Pure, so it is unit-tested directly.
 */

export interface ParsedAmount {
  value: number;
  /** True when the text hedged ("about", "roughly") or gave a range. */
  approximate: boolean;
  /** Plain-language note when the parser made a choice, e.g. a range midpoint. */
  note?: string;
}

const HEDGES = /\b(about|around|roughly|approximately|approx\.?|maybe|almost|nearly|close to|just under|just over|over|under|ish)\b|~/gi;
const NONE = /^(none|nothing|zero|no|n\/a|na|nil|0)$/i;

const SCALE: Array<[RegExp, number]> = [
  [/^(b|bn|billion|billions)$/i, 1e9],
  [/^(m|mm|mil|mill|million|millions)$/i, 1e6],
  [/^(k|thousand|thousands|grand)$/i, 1e3],
];

function scaleFor(word: string | undefined): number {
  if (!word) return 1;
  for (const [re, n] of SCALE) if (re.test(word)) return n;
  return 1;
}

/** One number with an optional scale word: "1.2m", "400 k", "40 thousand". */
const NUM = /(\d[\d,]*(?:\.\d+)?|\.\d+)\s*(billion|billions|bn|b|million|millions|mill|mil|mm|m|thousand|thousands|grand|k)?\b/i;

export function parseAmount(input: string): ParsedAmount | null {
  let text = input.trim();
  if (!text) return null;
  if (NONE.test(text.replace(/[.!]$/, ''))) return { value: 0, approximate: false };

  const hedged = HEDGES.test(text);
  HEDGES.lastIndex = 0;
  let negative = /^\s*-|^\s*\(.*\)\s*$|\b(loss|lost|negative|in the red|deficit)\b/i.test(text);

  text = text
    .replace(HEDGES, ' ')
    .replace(/\b(usd|dollars?|bucks|per year|a year|annually|per month|a month|monthly|\/yr|\/mo)\b/gi, ' ')
    .replace(/[$€£]/g, ' ')
    .replace(/^\s*\(|\)\s*$/g, ' ');

  if (/\bhalf (a|of a) million\b/i.test(text)) return { value: negative ? -5e5 : 5e5, approximate: true };
  if (/\ba (million|mil)\b/i.test(text)) return { value: negative ? -1e6 : 1e6, approximate: hedged };

  // Range: "250-300k", "250k to 300k", "between 1 and 2 million".
  const range = text.match(
    /(\d[\d,]*(?:\.\d+)?)\s*(billion|bn|b|million|mil|mm|m|thousand|grand|k)?\s*(?:-|–|to|and)\s*(\d[\d,]*(?:\.\d+)?)\s*(billion|bn|b|million|mil|mm|m|thousand|grand|k)?\b/i,
  );
  if (range) {
    const hiScale = scaleFor(range[4]);
    const loScale = range[2] ? scaleFor(range[2]) : hiScale;
    const lo = Number(range[1]!.replace(/,/g, '')) * loScale;
    const hi = Number(range[3]!.replace(/,/g, '')) * hiScale;
    if (Number.isFinite(lo) && Number.isFinite(hi) && hi >= lo) {
      const mid = Math.round((lo + hi) / 2);
      return {
        value: negative ? -mid : mid,
        approximate: true,
        note: `Used the middle of ${fmtMoney(lo)}-${fmtMoney(hi)}.`,
      };
    }
  }

  const m = text.match(NUM);
  if (!m) return null;
  const n = Number(m[1]!.replace(/,/g, '')) * scaleFor(m[2]);
  if (!Number.isFinite(n)) return null;
  if (n === 0) negative = false;
  return { value: negative ? -Math.round(n) : Math.round(n), approximate: hedged };
}

/** Whole, non-negative counts, e.g. jobs. */
export function parseCount(input: string): number | null {
  const p = parseAmount(input);
  if (!p || p.value < 0) return null;
  return Math.round(p.value);
}

export function fmtMoney(n: number): string {
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(a % 1e6 === 0 ? 0 : 2).replace(/\.?0+$/, '')}M`;
  return `${sign}$${Math.round(a).toLocaleString('en-US')}`;
}
